/**
 * Serviço de Detecção de Privacidade e Ambientes Suspeitos
 * Responsável por identificar recursos de privacidade, VPNs, proxies e ambientes automatizados
 */

import { IPGeolocationService } from './IPGeolocationService.js';

export class PrivacyService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('PrivacyService');
        this.cache = new Map();
        this.cacheTimeout = 180000; // 3 minutos
        this.stats = {
            detectionsPerformed: 0,
            suspiciousEnvironmentsDetected: 0,
            privacyFeaturesDetected: 0,
            cacheHits: 0,
            cacheMisses: 0
        };
        this.detectionMethods = {
            incognito: [
                this.detectIncognitoModern.bind(this),
                this.detectIncognitoChrome.bind(this),
                this.detectIncognitoFirefox.bind(this),
                this.detectIncognitoSafari.bind(this),
                this.detectIncognitoEdge.bind(this)
            ],
            automation: [
                this.detectWebDriver.bind(this),
                this.detectPhantomJS.bind(this),
                this.detectSelenium.bind(this),
                this.detectPuppeteer.bind(this),
                this.detectPlaywright.bind(this)
            ],
            privacy: [
                this.detectVPN.bind(this),
                this.detectVPNAdvanced.bind(this),
                this.detectProxy.bind(this),
                this.detectTor.bind(this),
                this.detectAdBlocker.bind(this),
                this.detectTrackingProtection.bind(this)
            ]
        };

        // Initialize IP Geolocation Service
        this.ipGeolocationService = new IPGeolocationService(config, logger);
    }

    /**
     * Detecta ambiente suspeito completo
     */
    async detectSuspiciousEnvironment() {
        const cacheKey = 'suspicious_environment';

        // Verifica cache
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                this.stats.cacheHits++;
                return cached.data;
            }
        }

        this.stats.cacheMisses++;
        this.stats.detectionsPerformed++;

        try {
            const detection = {
                // Detecção de modo incógnito
                incognito: await this.detectIncognitoMode(),

                // Detecção de automação
                automation: await this.detectAutomation(),

                // Detecção de recursos de privacidade
                privacy: await this.detectPrivacyFeatures(),

                // Detecção de extensões
                extensions: await this.detectPrivacyExtensions(),

                // Análise de rede
                network: await this.analyzeNetworkEnvironment(),

                // Análise de comportamento
                behavior: await this.analyzeSuspiciousBehavior(),

                // Pontuação de risco
                riskScore: 0,

                // Timestamp
                timestamp: Date.now()
            };

            // Calcula pontuação de risco
            detection.riskScore = this.calculateRiskScore(detection);

            // Atualiza estatísticas
            if (detection.riskScore.score > 50) {
                this.stats.suspiciousEnvironmentsDetected++;
            }

            if (detection.privacy.detected || detection.incognito.detected) {
                this.stats.privacyFeaturesDetected++;
            }

            // Armazena no cache
            this.cache.set(cacheKey, {
                data: detection,
                timestamp: Date.now()
            });

            this.logger.info('Detecção de ambiente suspeito concluída', {
                riskScore: detection.riskScore.score,
                level: detection.riskScore.level
            });

            return detection;

        } catch (error) {
            this.logger.error('Erro na detecção de ambiente suspeito', { error: error.message });
            throw error;
        }
    }

    /**
     * Detecta modo incógnito/privado
     */
    async detectIncognitoMode() {
        const results = {
            detected: false,
            confidence: 0,
            methods: [],
            details: {}
        };

        // Executa todos os métodos de detecção
        for (const method of this.detectionMethods.incognito) {
            try {
                const result = await method();
                if (result.detected) {
                    results.detected = true;
                    results.confidence = Math.max(results.confidence, result.confidence);
                    results.methods.push(result.method);
                    results.details[result.method] = result.details;
                }
            } catch (error) {
                this.logger.debug(`Erro no método de detecção ${method.name}`, { error: error.message });
            }
        }

        return results;
    }

    /**
     * Modern incognito detection using multiple signals
     */
    async detectIncognitoModern() {
        const indicators = [];

        try {
            // Method 1: Cache API quota (most reliable for Chrome 130+)
            if ('storage' in navigator && 'estimate' in navigator.storage) {
                const estimate = await navigator.storage.estimate();
                const quotaMB = Math.round((estimate.quota || 0) / (1024 * 1024));

                // Normal mode: ~5GB+, Incognito: ~120MB
                if (quotaMB < 200) {
                    indicators.push({
                        type: 'cache_quota',
                        value: quotaMB,
                        threshold: 200,
                        confidence: 90
                    });
                }
            }

            // Method 2: Performance entries (fewer entries in incognito due to no cache)
            if (window.performance && window.performance.getEntriesByType) {
                const resources = window.performance.getEntriesByType('resource');
                // In incognito, there are usually fewer cached resources
                if (resources.length < 2) {
                    indicators.push({
                        type: 'performance_entries',
                        value: resources.length,
                        threshold: 2,
                        confidence: 40
                    });
                }
            }

            // Method 3: Credential Management API behavior
            if (navigator.credentials && navigator.credentials.preventSilentAccess) {
                try {
                    await navigator.credentials.preventSilentAccess();
                    // If this succeeds without user gesture in incognito, it might indicate incognito
                } catch (e) {
                    indicators.push({
                        type: 'credentials_api',
                        value: 'blocked',
                        confidence: 50
                    });
                }
            }

            // Method 4: LocalStorage quota test
            try {
                const testKey = '__incognito_test__';
                const largeData = new Array(1024 * 1024).join('x'); // 1MB string
                localStorage.setItem(testKey, largeData);
                localStorage.removeItem(testKey);
            } catch (e) {
                if (e.name === 'QuotaExceededError' || e.code === 22) {
                    indicators.push({
                        type: 'localStorage_quota',
                        value: 'exceeded',
                        confidence: 70
                    });
                }
            }

        } catch (error) {
            this.logger.debug('Error in modern incognito detection', { error: error.message });
        }

        const detected = indicators.length > 0;
        const maxConfidence = indicators.length > 0 ? Math.max(...indicators.map(i => i.confidence)) : 0;

        return {
            detected,
            confidence: maxConfidence,
            method: 'modern_detection',
            details: { indicators, count: indicators.length }
        };
    }

    /**
     * Detecta incógnito no Chrome/Edge
     */
    async detectIncognitoChrome() {
        try {
            // Método 1: Storage quota
            if ('storage' in navigator && 'estimate' in navigator.storage) {
                const estimate = await navigator.storage.estimate();
                const quota = estimate.quota;

                // Em modo incógnito, a quota é muito menor
                if (quota && quota < 120000000) { // Menos que ~120MB
                    return {
                        detected: true,
                        confidence: 85,
                        method: 'chrome_storage_quota',
                        details: { quota, threshold: 120000000 }
                    };
                }
            }

            // Método 2: FileSystem API
            if ('webkitRequestFileSystem' in window) {
                return new Promise((resolve) => {
                    window.webkitRequestFileSystem(
                        window.TEMPORARY,
                        1,
                        () => resolve({
                            detected: false,
                            confidence: 0,
                            method: 'chrome_filesystem',
                            details: { available: true }
                        }),
                        () => resolve({
                            detected: true,
                            confidence: 80,
                            method: 'chrome_filesystem',
                            details: { available: false }
                        })
                    );
                });
            }

        } catch (error) {
            this.logger.debug('Erro na detecção Chrome incógnito', { error: error.message });
        }

        return { detected: false, confidence: 0, method: 'chrome', details: {} };
    }

    /**
     * Detecta incógnito no Firefox
     */
    async detectIncognitoFirefox() {
        try {
            // Método 1: IndexedDB
            if ('indexedDB' in window) {
                return new Promise((resolve) => {
                    const db = indexedDB.open('test');
                    db.onerror = () => resolve({
                        detected: true,
                        confidence: 90,
                        method: 'firefox_indexeddb',
                        details: { indexedDBBlocked: true }
                    });
                    db.onsuccess = () => {
                        db.result.close();
                        resolve({
                            detected: false,
                            confidence: 0,
                            method: 'firefox_indexeddb',
                            details: { indexedDBBlocked: false }
                        });
                    };
                });
            }

            // Método 2: ServiceWorker
            if ('serviceWorker' in navigator) {
                try {
                    await navigator.serviceWorker.register('data:,');
                    return {
                        detected: false,
                        confidence: 0,
                        method: 'firefox_serviceworker',
                        details: { serviceWorkerBlocked: false }
                    };
                } catch (error) {
                    return {
                        detected: true,
                        confidence: 75,
                        method: 'firefox_serviceworker',
                        details: { serviceWorkerBlocked: true, error: error.message }
                    };
                }
            }

        } catch (error) {
            this.logger.debug('Erro na detecção Firefox incógnito', { error: error.message });
        }

        return { detected: false, confidence: 0, method: 'firefox', details: {} };
    }

    /**
     * Detecta incógnito no Safari
     */
    async detectIncognitoSafari() {
        try {
            // Método 1: LocalStorage
            try {
                localStorage.setItem('test', '1');
                localStorage.removeItem('test');
                return {
                    detected: false,
                    confidence: 0,
                    method: 'safari_localstorage',
                    details: { localStorageBlocked: false }
                };
            } catch (error) {
                return {
                    detected: true,
                    confidence: 95,
                    method: 'safari_localstorage',
                    details: { localStorageBlocked: true, error: error.message }
                };
            }

        } catch (error) {
            this.logger.debug('Erro na detecção Safari incógnito', { error: error.message });
        }

        return { detected: false, confidence: 0, method: 'safari', details: {} };
    }

    /**
     * Detecta incógnito no Edge
     */
    async detectIncognitoEdge() {
        // Edge usa métodos similares ao Chrome
        return await this.detectIncognitoChrome();
    }

    /**
     * Detecta automação
     */
    async detectAutomation() {
        const results = {
            detected: false,
            confidence: 0,
            methods: [],
            details: {}
        };

        // Executa todos os métodos de detecção de automação
        for (const method of this.detectionMethods.automation) {
            try {
                const result = await method();
                if (result.detected) {
                    results.detected = true;
                    results.confidence = Math.max(results.confidence, result.confidence);
                    results.methods.push(result.method);
                    results.details[result.method] = result.details;
                }
            } catch (error) {
                this.logger.debug(`Erro no método de detecção ${method.name}`, { error: error.message });
            }
        }

        return results;
    }

    /**
     * Detecta WebDriver
     */
    async detectWebDriver() {
        const indicators = [];

        // Verifica propriedades do WebDriver
        if (navigator.webdriver === true) {
            indicators.push('navigator.webdriver = true');
        }

        if (window.webdriver === true) {
            indicators.push('window.webdriver = true');
        }

        // Verifica propriedades do Chrome WebDriver
        if (window.chrome && window.chrome.runtime && window.chrome.runtime.onConnect) {
            indicators.push('Chrome WebDriver runtime detected');
        }

        // Verifica variáveis globais do WebDriver
        const webdriverVars = [
            '__webdriver_evaluate',
            '__selenium_evaluate',
            '__webdriver_script_function',
            '__webdriver_script_func',
            '__webdriver_script_fn',
            '__fxdriver_evaluate',
            '__driver_evaluate',
            '__webdriver_unwrapped',
            '__webdriver_script_fn'
        ];

        webdriverVars.forEach(varName => {
            if (window[varName]) {
                indicators.push(`Global variable ${varName} detected`);
            }
        });

        // Verifica document properties
        if (document.$cdc_asdjflasutopfhvcZLmcfl_) {
            indicators.push('Chrome WebDriver $cdc_ property detected');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 30, 100),
            method: 'webdriver',
            details: { indicators }
        };
    }

    /**
     * Detecta PhantomJS
     */
    async detectPhantomJS() {
        const indicators = [];

        // Verifica propriedades específicas do PhantomJS
        if (window.callPhantom || window._phantom) {
            indicators.push('PhantomJS global variables detected');
        }

        // Verifica user agent
        if (navigator.userAgent.toLowerCase().includes('phantomjs')) {
            indicators.push('PhantomJS in user agent');
        }

        // Verifica propriedades do document
        if (document.__phantomas) {
            indicators.push('PhantomJS document property detected');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 40, 100),
            method: 'phantomjs',
            details: { indicators }
        };
    }

    /**
     * Detecta Selenium
     */
    async detectSelenium() {
        const indicators = [];

        // Verifica propriedades do Selenium
        if (window.selenium) {
            indicators.push('window.selenium detected');
        }

        // Verifica document properties
        const seleniumProps = [
            'webdriver',
            '__selenium_evaluate',
            '__selenium_unwrapped'
        ];

        seleniumProps.forEach(prop => {
            if (document[prop]) {
                indicators.push(`Document property ${prop} detected`);
            }
        });

        // Verifica HTML attributes
        if (document.documentElement.getAttribute('selenium')) {
            indicators.push('Selenium HTML attribute detected');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 35, 100),
            method: 'selenium',
            details: { indicators }
        };
    }

    /**
     * Detecta Puppeteer
     */
    async detectPuppeteer() {
        const indicators = [];

        // Verifica propriedades específicas do Puppeteer
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const permissionStatus = await navigator.permissions.query({ name: 'notifications' });
                if (permissionStatus.state === 'denied' && !('Notification' in window)) {
                    indicators.push('Puppeteer notification permission pattern');
                }
            } catch (error) {
                // Ignora erros
            }
        }

        // Verifica user agent patterns
        const userAgent = navigator.userAgent;
        if (userAgent.includes('HeadlessChrome')) {
            indicators.push('HeadlessChrome in user agent');
        }

        // Verifica propriedades do Chrome
        if (window.chrome && !window.chrome.runtime) {
            indicators.push('Chrome object without runtime (headless)');
        }

        // Verifica plugins
        if (navigator.plugins.length === 0) {
            indicators.push('No plugins detected (headless)');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 25, 100),
            method: 'puppeteer',
            details: { indicators }
        };
    }

    /**
     * Detecta Playwright
     */
    async detectPlaywright() {
        const indicators = [];

        // Verifica propriedades específicas do Playwright
        if (window.playwright) {
            indicators.push('window.playwright detected');
        }

        // Verifica user agent patterns
        const userAgent = navigator.userAgent;
        if (userAgent.includes('Playwright')) {
            indicators.push('Playwright in user agent');
        }

        // Verifica propriedades do navigator
        if (navigator.webdriver === undefined && window.chrome && !window.chrome.runtime) {
            indicators.push('Playwright-like environment detected');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 30, 100),
            method: 'playwright',
            details: { indicators }
        };
    }

    /**
     * Detecta recursos de privacidade
     */
    async detectPrivacyFeatures() {
        const results = {
            detected: false,
            confidence: 0,
            methods: [],
            details: {}
        };

        // Executa todos os métodos de detecção de privacidade
        for (const method of this.detectionMethods.privacy) {
            try {
                const result = await method();
                if (result.detected) {
                    results.detected = true;
                    results.confidence = Math.max(results.confidence, result.confidence);
                    results.methods.push(result.method);
                    results.details[result.method] = result.details;
                }
            } catch (error) {
                this.logger.debug(`Erro no método de detecção ${method.name}`, { error: error.message });
            }
        }

        return results;
    }

    /**
     * Detecta VPN
     */
    async detectVPN() {
        const indicators = [];

        // Verifica timezone vs localização esperada
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const language = navigator.language;

            // Lógica simplificada - pode ser expandida com base geográfica
            if (timezone && language) {
                const timezoneCountry = this.getCountryFromTimezone(timezone);
                const languageCountry = this.getCountryFromLanguage(language);

                if (timezoneCountry && languageCountry && timezoneCountry !== languageCountry) {
                    indicators.push(`Timezone/Language mismatch: ${timezoneCountry} vs ${languageCountry}`);
                }
            }
        } catch (error) {
            this.logger.debug('Erro na verificação de timezone/idioma', { error: error.message });
        }

        // Verifica WebRTC leak protection
        try {
            const hasWebRTCLeak = await this.checkWebRTCLeak();
            if (!hasWebRTCLeak) {
                indicators.push('WebRTC leak protection detected');
            }
        } catch (error) {
            indicators.push('WebRTC blocked or protected');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 20, 100),
            method: 'vpn',
            details: { indicators }
        };
    }

    /**
     * Advanced VPN detection using mDNS candidates, IP geolocation, and multiple signals
     */
    async detectVPNAdvanced() {
        const indicators = [];
        let confidence = 0;

        try {
            // Method 1: Check for mDNS candidates (obfuscated local addresses)
            // VPNs and privacy-focused browsers use mDNS to hide local IP
            try {
                const webRTCInfo = await this.getWebRTCInfoEnhanced();

                if (webRTCInfo.hasMdnsCandidates) {
                    indicators.push('mDNS candidates detected (IP obfuscation)');
                    confidence = Math.max(confidence, 70);
                }

                if (!webRTCInfo.hasLocalIP && !webRTCInfo.hasPublicIP) {
                    indicators.push('No IP addresses exposed via WebRTC');
                    confidence = Math.max(confidence, 60);
                }

                if (webRTCInfo.candidateCount === 0) {
                    indicators.push('WebRTC completely blocked');
                    confidence = Math.max(confidence, 50);
                }
            } catch (error) {
                indicators.push('WebRTC analysis failed');
                confidence = Math.max(confidence, 40);
            }

            // Method 2: IP Geolocation comparison
            try {
                // Check timezone vs IP location
                const timezoneMismatch = await this.ipGeolocationService.detectTimezoneIPMismatch();
                if (timezoneMismatch.detected) {
                    indicators.push(timezoneMismatch.reason);
                    confidence = Math.max(confidence, timezoneMismatch.confidence);
                }

                // Check language vs IP country
                const languageMismatch = await this.ipGeolocationService.detectLanguageCountryMismatch();
                if (languageMismatch.detected) {
                    indicators.push(languageMismatch.reason);
                    confidence = Math.max(confidence, languageMismatch.confidence);
                }

                // Check if IP is from VPN/Datacenter
                const vpnIP = await this.ipGeolocationService.detectVPNIP();
                if (vpnIP.detected) {
                    indicators.push(...vpnIP.indicators);
                    confidence = Math.max(confidence, vpnIP.confidence);
                }
            } catch (error) {
                this.logger.debug('IP geolocation check failed', { error: error.message });
            }

            // Method 3: Connection behavior analysis
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (connection) {
                // VPNs often show inconsistent latency patterns
                if (connection.rtt > 300 && connection.effectiveType === '4g') {
                    indicators.push(`High latency for 4G connection: ${connection.rtt}ms RTT`);
                    confidence = Math.max(confidence, 45);
                }

                // VPNs may show unusual downlink patterns
                if (connection.downlink < 1 && connection.effectiveType === '4g') {
                    indicators.push('Very low bandwidth for 4G connection');
                    confidence = Math.max(confidence, 40);
                }
            }

        } catch (error) {
            this.logger.error('Error in advanced VPN detection', { error: error.message });
        }

        return {
            detected: indicators.length > 0,
            confidence,
            method: 'vpn_advanced',
            details: { indicators, count: indicators.length }
        };
    }

    /**
     * Enhanced WebRTC info with mDNS detection
     */
    async getWebRTCInfoEnhanced() {
        return new Promise((resolve, reject) => {
            try {
                const RTCPeer = window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection;
                if (!RTCPeer) {
                    reject(new Error('WebRTC not supported'));
                    return;
                }

                const pc = new RTCPeer({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
                const info = {
                    hasLocalIP: false,
                    hasPublicIP: false,
                    hasMdnsCandidates: false,
                    localIPs: [],
                    publicIPs: [],
                    mdnsCandidates: [],
                    candidateCount: 0
                };

                pc.createDataChannel('');
                pc.createOffer().then(offer => pc.setLocalDescription(offer));

                pc.onicecandidate = (ice) => {
                    if (!ice || !ice.candidate) {
                        pc.close();
                        resolve(info);
                        return;
                    }

                    const candidate = ice.candidate.candidate;
                    info.candidateCount++;

                    // Check for mDNS candidates (obfuscated addresses like xxx.local)
                    if (candidate.includes('.local')) {
                        info.hasMdnsCandidates = true;
                        info.mdnsCandidates.push(candidate);
                    }

                    // Check for IP addresses
                    const ipv4Match = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3})/);
                    const ipv6Match = candidate.match(/([a-fA-F0-9:]+:+[a-fA-F0-9:]+)/);

                    if (ipv4Match) {
                        const ip = ipv4Match[1];
                        if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)) {
                            info.hasLocalIP = true;
                            info.localIPs.push(ip);
                        } else if (!ip.startsWith('0.') && !ip.startsWith('127.')) {
                            info.hasPublicIP = true;
                            info.publicIPs.push(ip);
                        }
                    }

                    if (ipv6Match && !ipv6Match[1].startsWith('fe80')) {
                        info.hasPublicIP = true;
                        info.publicIPs.push(ipv6Match[1]);
                    }
                };

                setTimeout(() => {
                    pc.close();
                    resolve(info);
                }, 3000);

            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Detecta proxy
     */
    async detectProxy() {
        const indicators = [];

        // Verifica headers suspeitos (limitado no browser)
        const userAgent = navigator.userAgent.toLowerCase();
        const proxyKeywords = ['proxy', 'squid', 'cache'];

        proxyKeywords.forEach(keyword => {
            if (userAgent.includes(keyword)) {
                indicators.push(`Proxy keyword "${keyword}" in user agent`);
            }
        });

        // Verifica conexão suspeita
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (connection && connection.effectiveType === 'slow-2g' && connection.downlink > 10) {
            indicators.push('Inconsistent connection speed (possible proxy)');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 25, 100),
            method: 'proxy',
            details: { indicators }
        };
    }

    /**
     * Detecta Tor
     */
    async detectTor() {
        const indicators = [];

        // Verifica user agent patterns típicos do Tor
        const userAgent = navigator.userAgent;

        // Tor Browser geralmente usa versões específicas do Firefox
        if (userAgent.includes('Firefox') && !userAgent.includes('Chrome')) {
            const firefoxVersion = userAgent.match(/Firefox\/(\d+)/)?.[1];
            if (firefoxVersion) {
                // Versões comuns do Tor Browser
                const torVersions = ['91', '102', '115'];
                if (torVersions.includes(firefoxVersion)) {
                    indicators.push(`Possible Tor Browser Firefox version: ${firefoxVersion}`);
                }
            }
        }

        // Verifica resolução de tela típica do Tor
        const screenWidth = screen.width;
        const screenHeight = screen.height;

        // Tor Browser força resoluções específicas
        const torResolutions = [
            [1000, 700], [1200, 800], [1400, 900], [1600, 900]
        ];

        const currentResolution = [screenWidth, screenHeight];
        const matchesTorResolution = torResolutions.some(([w, h]) =>
            Math.abs(w - screenWidth) < 50 && Math.abs(h - screenHeight) < 50
        );

        if (matchesTorResolution) {
            indicators.push(`Screen resolution matches Tor pattern: ${screenWidth}x${screenHeight}`);
        }

        // Verifica timezone UTC (comum no Tor)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone === 'UTC') {
            indicators.push('UTC timezone (common in Tor)');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 30, 100),
            method: 'tor',
            details: { indicators }
        };
    }

    /**
     * Detecta bloqueador de anúncios
     */
    async detectAdBlocker() {
        const indicators = [];

        try {
            // Método 1: Elemento de teste
            const testElement = document.createElement('div');
            testElement.innerHTML = '&nbsp;';
            testElement.className = 'adsbox ad-banner advertisement';
            testElement.style.position = 'absolute';
            testElement.style.left = '-10000px';
            testElement.style.width = '1px';
            testElement.style.height = '1px';

            document.body.appendChild(testElement);

            // Aguarda um pouco para o bloqueador agir
            await new Promise(resolve => setTimeout(resolve, 100));

            if (testElement.offsetHeight === 0 || testElement.style.display === 'none') {
                indicators.push('Ad element blocked by CSS');
            }

            document.body.removeChild(testElement);

            // Método 2: Verifica se recursos de anúncio são bloqueados
            const adUrls = [
                'https://googleads.g.doubleclick.net/pagead/ads',
                'https://www.google-analytics.com/analytics.js'
            ];

            for (const url of adUrls) {
                try {
                    const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
                    // Se chegou aqui, não foi bloqueado
                } catch (error) {
                    indicators.push(`Ad URL blocked: ${url}`);
                }
            }

        } catch (error) {
            this.logger.debug('Erro na detecção de ad blocker', { error: error.message });
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 40, 100),
            method: 'adblocker',
            details: { indicators }
        };
    }

    /**
     * Detecta proteção contra rastreamento
     */
    async detectTrackingProtection() {
        const indicators = [];

        // Verifica Do Not Track
        if (navigator.doNotTrack === '1') {
            indicators.push('Do Not Track enabled');
        }

        // Verifica Global Privacy Control
        if (navigator.globalPrivacyControl === true) {
            indicators.push('Global Privacy Control enabled');
        }

        // Verifica se cookies de terceiros são bloqueados
        try {
            document.cookie = 'test=1; SameSite=None; Secure';
            if (!document.cookie.includes('test=1')) {
                indicators.push('Third-party cookies blocked');
            }
            // Limpa o cookie de teste
            document.cookie = 'test=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        } catch (error) {
            indicators.push('Cookie setting blocked');
        }

        return {
            detected: indicators.length > 0,
            confidence: Math.min(indicators.length * 25, 100),
            method: 'tracking_protection',
            details: { indicators }
        };
    }

    /**
     * Detecta extensões de privacidade
     */
    async detectPrivacyExtensions() {
        const extensions = {
            detected: false,
            list: [],
            confidence: 0
        };

        // Lista de extensões conhecidas para detectar
        const knownExtensions = [
            { name: 'uBlock Origin', test: () => window.uBlockOrigin || document.querySelector('[data-ublock]') },
            { name: 'AdBlock Plus', test: () => window.adblockplus || document.querySelector('[data-adblockplus]') },
            { name: 'Ghostery', test: () => window.ghostery || window.GhosteryDetected },
            { name: 'Privacy Badger', test: () => window.privacyBadger },
            { name: 'DuckDuckGo Privacy Essentials', test: () => window.duckduckgo },
            { name: 'Disconnect', test: () => window.disconnect },
            { name: 'NoScript', test: () => window.noscript }
        ];

        for (const extension of knownExtensions) {
            try {
                if (extension.test()) {
                    extensions.detected = true;
                    extensions.list.push(extension.name);
                    extensions.confidence = Math.min(extensions.confidence + 20, 100);
                }
            } catch (error) {
                // Ignora erros de teste
            }
        }

        return extensions;
    }

    /**
     * Analisa ambiente de rede
     */
    async analyzeNetworkEnvironment() {
        const analysis = {
            suspicious: false,
            factors: [],
            details: {}
        };

        try {
            // Analisa informações de conexão
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (connection) {
                analysis.details.connection = {
                    effectiveType: connection.effectiveType,
                    downlink: connection.downlink,
                    rtt: connection.rtt,
                    saveData: connection.saveData
                };

                // Verifica inconsistências suspeitas
                if (connection.effectiveType === '4g' && connection.downlink < 1) {
                    analysis.suspicious = true;
                    analysis.factors.push('Inconsistent 4G speed');
                }

                if (connection.rtt > 1000 && connection.effectiveType === '4g') {
                    analysis.suspicious = true;
                    analysis.factors.push('High latency for 4G connection');
                }
            }

            // Verifica WebRTC
            try {
                const webRTCInfo = await this.getWebRTCInfo();
                analysis.details.webRTC = webRTCInfo;

                if (!webRTCInfo.localIP && !webRTCInfo.publicIP) {
                    analysis.suspicious = true;
                    analysis.factors.push('WebRTC completely blocked');
                }
            } catch (error) {
                analysis.suspicious = true;
                analysis.factors.push('WebRTC access denied');
            }

        } catch (error) {
            this.logger.debug('Erro na análise de rede', { error: error.message });
        }

        return analysis;
    }

    /**
     * Analisa comportamento suspeito
     */
    async analyzeSuspiciousBehavior() {
        const analysis = {
            suspicious: false,
            factors: [],
            details: {}
        };

        // Verifica velocidade de carregamento da página
        if (performance.timing) {
            const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
            analysis.details.loadTime = loadTime;

            if (loadTime < 100) {
                analysis.suspicious = true;
                analysis.factors.push('Extremely fast page load (possible automation)');
            }
        }

        // Verifica interação do usuário
        const hasUserInteraction = this.checkUserInteraction();
        analysis.details.userInteraction = hasUserInteraction;

        if (!hasUserInteraction) {
            analysis.suspicious = true;
            analysis.factors.push('No user interaction detected');
        }

        // Verifica padrões de mouse/teclado (se disponível)
        const behaviorPatterns = this.analyzeBehaviorPatterns();
        analysis.details.behaviorPatterns = behaviorPatterns;

        if (behaviorPatterns.suspicious) {
            analysis.suspicious = true;
            analysis.factors.push(...behaviorPatterns.factors);
        }

        return analysis;
    }

    /**
     * Verifica interação do usuário
     */
    checkUserInteraction() {
        // Verifica se houve eventos de mouse ou teclado
        let hasInteraction = false;

        const interactionEvents = ['click', 'keydown', 'mousemove', 'touchstart'];

        interactionEvents.forEach(eventType => {
            document.addEventListener(eventType, () => {
                hasInteraction = true;
            }, { once: true, passive: true });
        });

        return hasInteraction;
    }

    /**
     * Analisa padrões comportamentais
     */
    analyzeBehaviorPatterns() {
        const patterns = {
            suspicious: false,
            factors: []
        };

        // Verifica se há dados comportamentais disponíveis
        // Esta função seria integrada com o BehavioralService

        return patterns;
    }

    /**
     * Obtém informações WebRTC
     */
    async getWebRTCInfo() {
        return new Promise((resolve, reject) => {
            try {
                const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
                const info = {
                    localIP: null,
                    publicIP: null,
                    candidates: []
                };

                pc.createDataChannel('');
                pc.createOffer().then(pc.setLocalDescription.bind(pc));

                pc.onicecandidate = (ice) => {
                    if (!ice || !ice.candidate) {
                        pc.close();
                        resolve(info);
                        return;
                    }

                    const candidate = ice.candidate.candidate;
                    info.candidates.push(candidate);

                    const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3})/);
                    if (ipMatch) {
                        const ip = ipMatch[1];
                        if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.')) {
                            info.localIP = ip;
                        } else {
                            info.publicIP = ip;
                        }
                    }
                };

                setTimeout(() => {
                    pc.close();
                    resolve(info);
                }, 3000);

            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Verifica vazamento WebRTC
     */
    async checkWebRTCLeak() {
        try {
            const info = await this.getWebRTCInfo();
            return info.localIP || info.publicIP;
        } catch (error) {
            return false;
        }
    }

    /**
     * Obtém país do timezone
     */
    getCountryFromTimezone(timezone) {
        // Mapeamento simplificado - pode ser expandido
        const timezoneMap = {
            'America/New_York': 'US',
            'America/Los_Angeles': 'US',
            'Europe/London': 'GB',
            'Europe/Paris': 'FR',
            'Europe/Berlin': 'DE',
            'Asia/Tokyo': 'JP',
            'Asia/Shanghai': 'CN',
            'America/Sao_Paulo': 'BR'
        };

        return timezoneMap[timezone] || null;
    }

    /**
     * Obtém país do idioma
     */
    getCountryFromLanguage(language) {
        // Mapeamento simplificado
        const langMap = {
            'en-US': 'US',
            'en-GB': 'GB',
            'fr-FR': 'FR',
            'de-DE': 'DE',
            'ja-JP': 'JP',
            'zh-CN': 'CN',
            'pt-BR': 'BR'
        };

        return langMap[language] || language.split('-')[1] || null;
    }

    /**
     * Calcula pontuação de risco
     */
    calculateRiskScore(detection) {
        let score = 0;
        const factors = [];

        // Pontuação por categoria
        if (detection.incognito.detected) {
            score += detection.incognito.confidence * 0.3;
            factors.push(`Modo incógnito detectado (${detection.incognito.confidence}%)`);
        }

        if (detection.automation.detected) {
            score += detection.automation.confidence * 0.4;
            factors.push(`Automação detectada (${detection.automation.confidence}%)`);
        }

        if (detection.privacy.detected) {
            score += detection.privacy.confidence * 0.2;
            factors.push(`Recursos de privacidade detectados (${detection.privacy.confidence}%)`);
        }

        if (detection.extensions.detected) {
            score += detection.extensions.confidence * 0.1;
            factors.push(`Extensões de privacidade: ${detection.extensions.list.join(', ')}`);
        }

        if (detection.network.suspicious) {
            score += 20;
            factors.push(`Ambiente de rede suspeito: ${detection.network.factors.join(', ')}`);
        }

        if (detection.behavior.suspicious) {
            score += 15;
            factors.push(`Comportamento suspeito: ${detection.behavior.factors.join(', ')}`);
        }

        // Normaliza a pontuação
        score = Math.min(Math.round(score), 100);

        // Determina nível de risco
        let level = 'low';
        if (score >= 70) level = 'high';
        else if (score >= 40) level = 'medium';

        return {
            score,
            level,
            factors
        };
    }

    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            detectionsPerformed: this.stats.detectionsPerformed,
            suspiciousEnvironmentsDetected: this.stats.suspiciousEnvironmentsDetected,
            privacyFeaturesDetected: this.stats.privacyFeaturesDetected,
            cacheHits: this.stats.cacheHits,
            cacheMisses: this.stats.cacheMisses,
            cacheSize: this.cache.size,
            hitRate: this.stats.cacheHits + this.stats.cacheMisses > 0 ?
                (this.stats.cacheHits / (this.stats.cacheHits + this.stats.cacheMisses) * 100).toFixed(2) + '%' : '0%'
        };
    }

    /**
     * Limpa cache
     */
    clearCache() {
        this.cache.clear();
        this.logger.debug('Cache do PrivacyService limpo');
    }

    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        this.clearCache();
        this.logger.debug('Configuração do PrivacyService atualizada');
    }

    /**
     * Testa suporte a detecção de privacidade
     */
    static isSupported() {
        return !!(navigator && window && document);
    }

    /**
     * Obtém capacidades de detecção
     */
    static getCapabilities() {
        return {
            supported: PrivacyService.isSupported(),
            incognitoDetection: !!navigator.storage,
            automationDetection: true,
            webRTCDetection: !!(window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection),
            permissionsAPI: !!navigator.permissions,
            storageAPI: !!navigator.storage,
            connectionAPI: !!(navigator.connection || navigator.mozConnection || navigator.webkitConnection)
        };
    }

    /**
     * Limpa recursos
     */
    destroy() {
        this.clearCache();
        this.logger.debug('PrivacyService destruído');
    }
}